import * as analytics from '@anm/analytic';
import PlayerBlockMemorized, { PlayerBlockProps } from '@anm/components/PlayerBlock';
import { Statuses } from '@anm/components/PlayerBlock/PlayerStatuses';
import UploadSuccessModalMobile from '@anm/components/PlayerBlock/UploadSuccessModalMobile';
import { SuccessIcon } from '@anm/components/PlayerBlock/Wrapper';
import { DragItem } from '@anm/components/drag-drop';
import ShareVideoPage from '@anm/components/share/ShareVideoPage';
import UploadProjectsModal from '@anm/components/uploads/UploadProjectsModal';
import getCloudfrontCdn from '@anm/helpers/getCloudfrontCdn';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import noop from '@anm/helpers/noop';
import useSwitchState from '@anm/hooks/useSwitchState';
import { uploadsActions, uploadsSelectors } from '@anm/store/modules/uploads';
import { memo, useCallback, useContext, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Player from '../../../components/hosting/Player';
import vtConfig from '../../../config';
import { brandActions } from '../../../store/modules/brands';
import { hostedVideoSelectors } from '../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../store/modules/project';
import { userSelectors } from '../../../store/modules/user';
import { videoFolderActions, videoFolderSelectors } from '../../../store/modules/videoFolder';
import { getLandingTabUrl } from '../../hosting/CustomizationSection/helpers';
import SaveToBrandModal from '../../my-projects/SaveToBrandModal';
import StreamModal from '../../stream/StreamModalWrapper';
import useNotStableWarning from '../MyStreamsSection/hooks/useNotStableWarning';
import useStorageError from '../MyStreamsSection/hooks/useStorageError';
import { ProjectsDragDropPayload } from '../ProjectFolderBlock';
import useProjectsPermissions from '../hooks/useProjectsPermissions';

import AlreadyInsideAlert from './AlreadyInsideAlert';
import DeleteProjectModal from './DeleteProjectModal';
import DuplicateStreamModal from './DuplicateStreamModal';
import { useDuplicateVideoAlert } from './DuplicateVideoAlert';
import { useEditBigVideoAlert } from './EditBigVideoAlert';
import EditEmbeddedAlert from './EditEmbeddedAlert';
import HaveNotHostedAlert from './HaveNotHostedAlert';
import { ProjectFolderTreeModalContext } from './ProjectFolderTreeModalProvider';
import { Wrapper } from './Wrapper';
import areProjectPlayerBlockPropsEqual from './helpers/areProjectPlayerBlockPropsEqual';
import getEditLink from './helpers/getEditLink';
import isProjectBig from './helpers/isProjectBig';
import useCustomizationTabLink from './hooks/useCustomizationTabLink';
import useDownloadVideoHandler from './hooks/useDownloadVideoHandler';
import useDuplicateHandlers from './hooks/useDuplicateHandlers';
import { useHostedVideoManifestFetch } from './hooks/useHostedVideoManifestFetchOnHover';
import useHostedVideoSmallestPreview from './hooks/useHostedVideoSmallestPreview';
import useManifestPreview from './hooks/useManifestPreview';
import useManifestSources from './hooks/useManifestSources';
import usePlayerBottomHeight from './hooks/usePlayerBottomHeight';
import useProjectDropdownActions from './hooks/useProjectDropdownActions';
import useProjectPreview from './hooks/useProjectPreview';
import useProjectsModal from './hooks/useProjectsModal';
import useScrollToUploadingVideo from './hooks/useScrollToUploadingVideo';
import useStreamModal from './hooks/useStreamModal';
import useUpdatePreview from './hooks/useUpdatePreview';
import useUploadProgress from './hooks/useUploadProgress';
import useUploadSuccessModal from './hooks/useUploadSuccessModal';

export type ProjectPlayerBlockProps = Pick<
  PlayerBlockProps,
  'title' | 'width' | 'height' | 'hasTag' | 'modified' | 'onRename' | 'canStreamVideo' | 'streamId'
> & {
  version: number;
  duration: number;
  statuses: Statuses;
  projectId: string;
  hostId?: string;
  canEdit?: boolean;
  videoSrc?: string;
  shouldUpdatePreview?: boolean;
};

const { urls } = vtConfig();

const ProjectPlayerBlock: FC<ProjectPlayerBlockProps> = ({
  title,
  width,
  height,
  hostId,
  hasTag,
  version,
  canEdit,
  streamId,
  videoSrc,
  duration,
  statuses,
  projectId,
  canStreamVideo,
  shouldUpdatePreview,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const { canEditProject } = useProjectsPermissions();

  const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useSwitchState();
  const [isAlreadyInsideAlertOpen, openAlreadyInsideAlert, closeAlreadyInsideAlert] = useSwitchState();
  const [isHaveNotHostedAlertOpen, openHaveNotHostedAlert, closeHaveNotHostedAlert] = useSwitchState();
  const [isDuplicateStreamModal, openDuplicateStreamModal, closeDuplicateStreamModal] = useSwitchState();
  const [isProjectsModal, uploadProjects, onProjectsModalClose] = useProjectsModal(projectId, closeDeleteModal);

  const [isStreamModalOpened, openStreamModal, closeStreamModal] = useStreamModal(hostId);
  const [isAddToBrandModalOpened, openAddtoBrandModal, closeAddTobrandModal] = useSwitchState();

  const [isFieldFocused, setFieldFocused] = useState(false);
  const [isShareModalOpen, openShareModal, closeShareModal] = useSwitchState();
  const [isEditEmbeddedAlertOpen, openEditEmbeddedModal, closeEditEmbeddedModal] = useSwitchState();
  const [videoPreviewRef, videoPreview] = useHostedVideoSmallestPreview(hostId);
  const getHostedVideoManifest = useHostedVideoManifestFetch(hostId);

  const bestQualityVideo = useSelector(hostedVideoSelectors.selectBestQualityVideo);

  const [editBigVideoAlert, openEditBigVideoAlert] = useEditBigVideoAlert();
  const [duplicateAlert, openDuplicateAlert] = useDuplicateVideoAlert();
  const [handleDuplicate, handleDuplicateAndEdit] = useDuplicateHandlers({
    version,
    duration,
    projectId,
    openDuplicateAlert
  });

  const [fetchStorage, renderStorageModal] = useStorageError({ onSuccess: openStreamModal, actionType: 'stream' });
  const { checkUpload, renderWarningModal, isNotStable } = useNotStableWarning({ projectId, onSuccess: fetchStorage });

  const isUserStaff = useSelector(userSelectors.selectIsStaffUser);
  const canEditBrand = useSelector(userSelectors.selectCanEditBrand);
  const currentFolderId = useSelector(videoFolderSelectors.selectCurrentFolderId);
  const moveProjectToOtherWorkspacePending = useSelector(state =>
    videoFolderSelectors.selectMoveProjectToOtherWorkspacePending(state, projectId)
  );

  const { openFolderTree } = useContext(ProjectFolderTreeModalContext);
  const isDuplicatePending = useSelector(state => videoFolderSelectors.selectProjectDuplicateStatus(state, projectId));

  const upload = useSelector(uploadsSelectors.selectLatestUpload);
  const { moveProject, updateProjectMeta } = videoFolderActions;
  const isProjectPending = isDuplicatePending || moveProjectToOtherWorkspacePending;

  const openFolderTreeWithProps = () =>
    !isAlreadyInsideAlertOpen &&
    openFolderTree({
      moveItemName: 'video',
      onMoveFolder: handleMoveProject
    });

  const preview = useProjectPreview(projectId);

  const onRename = (newName: string) => newName && dispatch(updateProjectMeta({ projectId, meta: { name: newName } }));

  const canRenderEditLink = !statuses.isEmbedded && !isProjectBig({ version, duration });

  const renderEditLink = getEditLink(canRenderEditLink, projectId, () =>
    isProjectBig({ version, duration }) ? openEditBigVideoAlert(true) : openEditEmbeddedModal()
  );

  const renderLandingLink = useCustomizationTabLink({
    projectId,
    tab: 'landing',
    dataButton: 'landing page link'
  });
  const renderPermissionLink = useCustomizationTabLink({
    projectId,
    tab: 'permissions'
  });

  const renderSettingsLink = useCustomizationTabLink({
    projectId,
    tab: isMobileOrIPad() ? 'appearance' : 'preview',
    dataButton: 'setting link'
  });
  const renderEmbedLink = useCustomizationTabLink({
    projectId,
    onCanNotOpen: openEditEmbeddedModal
  });

  const showNotHostedShareModal = (isVideoHosted: boolean) => {
    openHaveNotHostedAlert();
    analytics.trackVideosShareTry(isVideoHosted);
  };

  const showHostedShareModal = (isVideoHosted: boolean) => {
    openShareModal();
    analytics.trackVideosShareTry(isVideoHosted);
  };

  const onShare = () =>
    checkUpload({
      cb: () => (hostId ? showHostedShareModal(!!hostId) : showNotHostedShareModal(!!hostId)),
      showWarning: false
    });

  const handleMoveProject = (folderId: number) => {
    return currentFolderId === folderId ? openAlreadyInsideAlert() : dispatch(moveProject({ folderId, projectId }));
  };

  const [showProgress, showSuccess, currentUploadProgress, isCreated] = useUploadProgress({
    hostId,
    projectId
  });

  const isNewVideo = upload?.hostId === hostId;
  const inProgress = isNewVideo && (showProgress || showSuccess);
  const isProjectDraggable = !isFieldFocused && !inProgress;

  useUpdatePreview({
    isCreated,
    shouldUpdatePreview,
    id: projectId
  });

  const [isUploadSuccessModalOpen, closeUploadSuccessModal] = useUploadSuccessModal(showSuccess);

  const [containerRef] = useScrollToUploadingVideo(inProgress);

  const handleServiceShare = (serviceName: string) => {
    analytics.trackVideosShareTo(serviceName);
  };

  const uploadedFile = useSelector(state => uploadsSelectors.selectUploadedFileByHostId(state, projectId));

  const handleCancelUpload = () =>
    dispatch(uploadsActions.cancelUploading({ uploadId: uploadedFile?.uploadId || '', hostId }));

  const onStreamVideo = useCallback(() => checkUpload({ showWarning: true }), []);
  const onAddToBrand = useCallback(() => checkUpload({ cb: openAddtoBrandModal, showWarning: false }), []);

  const handleDownload = useDownloadVideoHandler({ title, hostId, streamId });

  const isPublished = !!(preview && hostId);

  const hasSources = useManifestSources();

  const dropdownActions = useProjectDropdownActions({
    onShare,
    canEdit,
    streamId,
    projectId,
    isNotStable,
    isPublished,
    canEditBrand: !!canEditBrand,
    onAddToBrand,
    onStreamVideo,
    renderEditLink,
    renderLandingLink,
    renderSettingsLink,
    renderEmbedLink,
    onSettings: noop,
    onDownload: handleDownload,
    onDelete: openDeleteModal,
    canStreamVideo: hasSources,
    onDuplicate: () => checkUpload({ cb: handleDuplicate, showWarning: false }),
    onMove: openFolderTreeWithProps,
    onStreamEdit: openDuplicateStreamModal
  });

  const [bottomHeight, bottomRef] = usePlayerBottomHeight();

  const editorUrl = `${urls.editor}/#p=${projectId}`;
  const landingTabUrl = getLandingTabUrl(projectId);

  const videoPreviewSrc = getCloudfrontCdn({ url: videoSrc || videoPreview?.path || '', isLogged: true });

  const handleBrandChange = useCallback((brandId: number) => {
    dispatch(brandActions.updateCurrentBrandId(brandId));
  }, []);

  const manifestPreview = useManifestPreview();

  return (
    <Wrapper ref={containerRef}>
      <div ref={videoPreviewRef}>
        {isProjectsModal && (
          <UploadProjectsModal
            title={`Delete media`}
            description={`This media file is used in the following projects.<br> Please remove it from these projects first.`}
            projects={uploadProjects}
            onButtonClick={onProjectsModalClose}
          />
        )}
        {isDuplicateStreamModal && (
          <DuplicateStreamModal
            isPending={isDuplicatePending}
            onModalClose={closeDuplicateStreamModal}
            onRightButtonClick={handleDuplicateAndEdit}
          />
        )}
        {isUploadSuccessModalOpen && (
          <UploadSuccessModalMobile
            {...{ landingTabUrl }}
            onShare={openShareModal}
            onModalClose={closeUploadSuccessModal}
          />
        )}
        {isShareModalOpen && (
          <ShareVideoPage
            {...{ landingTabUrl }}
            shareLink={`${urls.videoLanding}${hostId}`}
            onClose={closeShareModal}
            onShare={handleServiceShare}
            onInit={closeUploadSuccessModal}
          />
        )}

        {isAddToBrandModalOpened && (
          <SaveToBrandModal onBrandChange={handleBrandChange} onClose={closeAddTobrandModal} projectId={projectId} />
        )}

        {isHaveNotHostedAlertOpen && (
          <HaveNotHostedAlert editorUrl={`${editorUrl}&target=publish`} onClose={closeHaveNotHostedAlert} />
        )}

        {isEditEmbeddedAlertOpen && (
          <EditEmbeddedAlert {...{ editorUrl }} onCancel={handleDuplicateAndEdit} onClose={closeEditEmbeddedModal} />
        )}

        {isAlreadyInsideAlertOpen && <AlreadyInsideAlert {...{ projectId }} onClose={closeAlreadyInsideAlert} />}

        {isDeleteModalOpen && <DeleteProjectModal {...{ projectId }} onClose={closeDeleteModal} />}

        {duplicateAlert}
        {editBigVideoAlert}

        {showSuccess && <SuccessIcon {...{ bottomHeight }} />}

        {isStreamModalOpened && (
          <StreamModal
            isHosted
            {...{ projectId }}
            initPreview={manifestPreview}
            videoSrc={bestQualityVideo}
            onClose={closeStreamModal}
          />
        )}
        {renderStorageModal()}
        {renderWarningModal()}
        <DragItem<ProjectsDragDropPayload>
          payload={{ projectId }}
          draggable={isProjectDraggable}
          dragPreview={preview || ''}
        >
          <PlayerBlockMemorized
            {...{
              title,
              width,
              height,
              preview,
              streamId,
              statuses,
              hostId,
              duration,
              bottomRef,
              showProgress,
              onShare,
              onRename,
              renderEditLink,
              canStreamVideo,
              renderEmbedLink,
              renderLandingLink,
              renderPermissionLink
            }}
            hostedId={hostId}
            canEdit={canEditProject}
            hasTag={isUserStaff && hasTag}
            actions={dropdownActions}
            videoSrc={videoPreviewSrc}
            isPending={isProjectPending}
            uploadProgress={currentUploadProgress}
            onUploadCancel={handleCancelUpload}
            onFieldFocused={setFieldFocused}
            onPreviewClick={getHostedVideoManifest}
            actionsDividerIndex={3}
            renderVideoPlayer={playerSizes => <Player {...{ hostId, projectId, playerSizes }} />}
            {...otherProps}
          />
        </DragItem>
      </div>
    </Wrapper>
  );
};

export const ProjectPlayerBlockMemorized = memo(ProjectPlayerBlock, areProjectPlayerBlockPropsEqual);

const ProjectGuard: FC<ProjectPlayerBlockProps> = props => {
  const { projectId } = props;
  const isSearchActive = !!useSelector(projectSelectors.selectProjectsSearchKeyWord);

  const project = useSelector(state => videoFolderSelectors.selectProjectById(state, projectId));

  return <>{(project || isSearchActive) && <ProjectPlayerBlockMemorized {...props} />}</>;
};

export default ProjectGuard;
